/**
 * Relative Time Formatter
 * Wraps Intl.RelativeTimeFormat for "X days ago" style formatting.
 *
 * Despite the file name this module contains no collator — the string collator lives
 * in `format.sorting.ts`.
 *
 * @module @infra/format
 */

import { getLanguageLocale } from '@infra/i18n';
import type { DateTimeInput, RelativeTimeUnit } from './format.types';

const formattersCache = new Map<string, Intl.RelativeTimeFormat>();

/**
 * Format relative time (e.g., "2 days ago", "in 3 hours")
 * @param value - Number of units (positive for future, negative for past)
 * @param unit - Time unit (day, hour, minute, etc.)
 * @param language - UI language code; any of the platform's supported codes
 * @returns Formatted relative time string
 */
export function formatRelativeTime(
  value: number,
  unit: RelativeTimeUnit,
  language: string
): string {
  const locale = getLanguageLocale(language);
  const cacheKey = `${locale}:${unit}`;

  if (!formattersCache.has(cacheKey)) {
    formattersCache.set(
      cacheKey,
      new Intl.RelativeTimeFormat(locale, {
        numeric: 'auto', // Use 'today' instead of 'in 0 days'
        style: 'long', // 'long' format
      })
    );
  }

  return formattersCache.get(cacheKey)!.format(value, unit);
}

/**
 * Thresholds for picking the unit in `formatRelativeFromNow`, largest unit last.
 * Each `amount` is how many of the current unit make up the next one.
 */
const DIVISIONS: Array<{ amount: number; unit: Intl.RelativeTimeFormatUnit }> = [
  { amount: 60, unit: 'second' },
  { amount: 60, unit: 'minute' },
  { amount: 24, unit: 'hour' },
  { amount: 7, unit: 'day' },
  { amount: 4.34524, unit: 'week' },
  { amount: 12, unit: 'month' },
  { amount: Number.POSITIVE_INFINITY, unit: 'year' },
];

/**
 * "5 minutes ago" / "il y a 5 minutes" for an instant, choosing the unit itself.
 *
 * Lives here rather than in the pages that need it: the unit thresholds are a
 * property of the product, not of one screen, and two screens picking different
 * thresholds is exactly the drift this module exists to prevent.
 *
 * Returns `''` for missing or unparseable input.
 */
export function formatRelativeFromNow(value: DateTimeInput, language: string): string {
  if (value === null || value === undefined) return '';
  const date = value instanceof Date ? value : new Date(value);
  const time = date.getTime();
  if (Number.isNaN(time)) return '';

  let duration = (time - Date.now()) / 1000;
  for (const division of DIVISIONS) {
    if (Math.abs(duration) < division.amount) {
      return formatRelativeTime(Math.round(duration), division.unit, language);
    }
    duration /= division.amount;
  }
  return '';
}
